import type { CaptureKind, Item } from '@mystiq/contracts';
import { ApiError, type Api } from '../api/client.ts';
import { sha256Hex } from '../lib/hash.ts';

/**
 * Sends a finished recording to Mystiq as a voice note or meeting. Every
 * attempt for one recording uses the same upload key, so sending again after
 * a lost connection continues the same upload instead of making a second
 * item; the server checks the bytes against their SHA-256. An upload that can
 * no longer take the bytes (its link ran out overnight, or it was closed) is
 * first finished if it already has them (its answer was lost), otherwise
 * started again under a new key.
 */
export async function saveRecording(
  api: Api,
  ws: string,
  recording: { blob: Blob; title: string; filename: string; capture: CaptureKind; uploadKey: string },
  options: { renewKey(): Promise<string>; folderId?: string },
): Promise<Item> {
  if (!recording.blob.size) throw new Error('Nothing was recorded.');
  const sha256 = await sha256Hex(recording.blob);
  let key = recording.uploadKey;
  for (let attempt = 0; attempt < 3; attempt++) {
    const upload = await api.beginUpload(ws, {
      filename: recording.filename,
      title: recording.title,
      sizeBytes: recording.blob.size,
      mimeType: recording.blob.type || 'audio/webm',
      capture: recording.capture,
      idempotencyKey: key,
      ...(sha256 ? { sha256 } : {}),
      ...(options.folderId ? { folderId: options.folderId } : {}),
    });
    const finished = () => api.completeUpload(ws, upload.uploadId).catch(() => null);
    if (Date.parse(upload.expiresAt) - Date.now() < 60_000) {
      const item = await finished();
      if (item) return item;
      key = await options.renewKey();
      continue;
    }
    try {
      await api.putUploadBytes(upload, recording.blob);
    } catch (error) {
      const closed = error instanceof ApiError && (error.code === 'CONFLICT' || error.code === 'UNAUTHENTICATED');
      if (!closed) throw error;
      const item = await finished();
      if (item) return item;
      key = await options.renewKey();
      continue;
    }
    return api.completeUpload(ws, upload.uploadId);
  }
  throw new Error('The recording could not be saved. It is kept in this browser: download a copy, or try again later.');
}
